/* hk-cart-min-qty.js — a designed cart line cannot be lowered below its minimum quantity.
 *
 * Order 26-35318 (2026-10-04): a 10-minimum custom bat bag was checked out with quantity 1. The design
 * tool enforces the minimum; the cart's quantity box did not. Liquid (snippets/hk-cart-min-qty.liquid)
 * stamps data-hk-min-qty on the quantity inputs of lines that have a minimum — the cart page (mobile +
 * desktop) and the mini-cart. This script is the enforcement. Lines without the attribute are untouched.
 *
 * WHERE it enforces (Codex review 2026-10-05 refuted an events-only version): the AJAX cart sends every
 * quantity through the input's `onchange` handler — the inline attribute, booster.js's debounced oninput
 * (`inp.onchange(e)`, a direct call that fires NO change event) and its +/- handler
 * (`qtyInput.onchange({target})`) — and from there through BoosterTheme.cart.updateQuantity. The guard
 * wraps BOTH: each guarded input's onchange, and the theme's updateQuantity itself (and cartPage's, which
 * the non-AJAX cart's +/- calls directly). A quantity under the minimum never reaches the theme's update,
 * whichever path asked for it.
 *   - mid-edit (the box has focus, e.g. "1" on the way to "12", or "12" cut back to "1"): nothing is sent
 *     and the box is left alone; the value is settled when the customer leaves the box
 *   - otherwise: the value becomes the minimum, the customer is told, and the update runs with it
 * The cart markup is replaced wholesale after every update, so new inputs are wrapped as they appear
 * (MutationObserver) and again, idempotently, on every event that touches one.
 *
 * Also: the minus button at the minimum does nothing but explain; a committed value under the minimum
 * (blur / Enter) becomes the minimum; a non-AJAX form submit cannot carry a lower updates[] value; the
 * remove button is untouched — removing the item is always allowed.
 *
 * Feedback is text, not colour: the visible "Minimum quantity" note is emphasised and the same sentence
 * goes to a polite live region. This is a storefront guard, not a checkout rule.
 */
(function () {
  'use strict';
  var SEL = 'input[data-hk-min-qty]';

  function minOf(input) {
    var n = parseInt(input.getAttribute('data-hk-min-qty'), 10);
    return n > 1 ? n : 0;
  }
  function boxOf(el) { return el && el.closest ? el.closest('.quantity--input') : null; }
  function inputIn(box) { return box ? box.querySelector(SEL) : null; }
  // the minus button is the one BEFORE the input (the theme's class names are inverted: its "-" button
  // carries quantity--input__incr), so identify it by position, not by class
  function isMinus(btn, input) {
    return !!(btn.compareDocumentPosition(input) & Node.DOCUMENT_POSITION_FOLLOWING);
  }
  // the WHOLE numeric value: parseInt reads "10e-1" as 10 (it is 1) and "1e2" as 1 (it is 100). Anything
  // that is not a whole number — empty, text, 9.9 — is no quantity (null), and counts as under the minimum.
  function valueOf(input) {
    var raw = String(input.value == null ? '' : input.value).trim();
    if (raw === '') return null;
    var v = Number(raw);
    // …and nothing a cart could hold: past 999999 String(v) stops being plain digits ("1e+21") or exact
    return (isFinite(v) && Math.floor(v) === v && Math.abs(v) <= 999999) ? v : null;
  }
  // "Under the minimum" means the customer is taking it there. A box still showing the quantity it was
  // RENDERED with (defaultValue = what the cart already holds — a cart made before this guard existed) is
  // left exactly as it is: never lowered, and never silently raised to the minimum at Checkout
  // (review round 3: an untouched under-minimum line was posted as updates[]=10).
  function under(input) {
    var min = minOf(input), v = valueOf(input);
    if (!min || (v !== null && v >= min)) return false;
    return String(input.value).trim() !== held(input);
  }
  // what the cart holds for this box, as far as this page knows: the last quantity it let through, else
  // the quantity it was rendered with. Without the first half, a line corrected up to its minimum could
  // be typed back down to its OLD rendered value before the theme re-rendered it (review round 4, D4).
  function held(input) {
    if (input.__hkHeld != null) return input.__hkHeld;
    return String(input.defaultValue == null ? '' : input.defaultValue).trim();
  }
  // a valid quantity leaves in canonical form: "1e1" and "12.0" are 10 and 12 to Number(), but the cart
  // endpoint's reading of them is not ours to assume (review round 3)
  function canonical(input) {
    var v = valueOf(input);
    if (v !== null && String(input.value).trim() !== String(v)) input.value = String(v);
  }
  // the mobile and desktop boxes of one line share a key; the theme copies a value between them
  function syncSiblings(input) {
    var key = input.getAttribute('data-variant-id');
    if (!key) return syncMinus(input);
    var list = document.querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) if (list[i].getAttribute('data-variant-id') === key) syncMinus(list[i]);
  }

  var live = null;
  function liveRegion() {
    if (live && live.isConnected) return live;
    live = document.createElement('div');
    live.className = 'hk-sr-only hk-cart-min-live';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    // .hk-sr-only comes from snippets/hk-accessibility.liquid; repeated inline so the region is never visible without it
    live.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0';
    document.body.appendChild(live);
    return live;
  }
  function tell(input, min) {
    var msg = 'Minimum quantity for this design is ' + min + '. To take it out of your cart, use the remove button.';
    var region = liveRegion();
    region.textContent = '';
    window.setTimeout(function () { region.textContent = msg; }, 50); // a changed node is what gets announced
    var note = boxOf(input);
    note = note ? note.nextElementSibling : null; // Liquid prints the note right after the box
    while (note && !(note.classList && note.classList.contains('hk-cart-min'))) note = note.nextElementSibling;
    if (note) {
      note.style.fontWeight = '700';
      window.setTimeout(function () { note.style.fontWeight = ''; }, 2500);
    }
  }
  function syncMinus(input) {
    var min = minOf(input), box = boxOf(input);
    if (!min || !box) return;
    var v = valueOf(input), buttons = box.querySelectorAll('button');
    for (var i = 0; i < buttons.length; i++) {
      if (!isMinus(buttons[i], input)) continue;
      if (v === null || v <= min) buttons[i].setAttribute('aria-disabled', 'true');
      else buttons[i].removeAttribute('aria-disabled');
    }
  }
  // settle a value under the minimum; true when it changed something
  function settle(input) {
    if (!under(input)) return false;
    var min = minOf(input);
    input.value = String(min);
    tell(input, min);
    return true;
  }

  // ---- the boundary: wrap the handler every theme path sends a quantity through ----
  // true = this call may go on to the theme; false = swallow it (mid-edit: send nothing, touch nothing)
  function admit(input) {
    if (!input || !input.matches || !input.matches(SEL) || !minOf(input)) return true;
    if (under(input)) {
      if (document.activeElement === input) { syncMinus(input); return false; }
      settle(input);
    }
    canonical(input);
    input.__hkHeld = String(input.value).trim(); // this is what goes to the cart now
    syncMinus(input);
    window.setTimeout(function () { syncSiblings(input); }, 0); // after the theme has copied the value across
    return true;
  }
  function guard(input) {
    if (!input || !minOf(input)) return;
    var send = input.onchange;
    // the marker is on the WRAPPER, not the input: if the theme (or anything) assigns a new handler over
    // ours, the next event wraps that one too (Codex round 2, B4)
    if (typeof send !== 'function' || send.__hkMinGuard) return;
    var wrapped = function () { return admit(input) ? send.apply(this, arguments) : undefined; };
    wrapped.__hkMinGuard = true;
    input.onchange = wrapped;
  }
  // The same rule at the theme's own send points, so it does not depend on an input having been wrapped
  // yet (a handler assigned late, markup inserted a moment ago — Codex round 2, B2/B6):
  //   BoosterTheme.cart.updateQuantity(event, variant, quantity, forced)  — the AJAX cart's /cart/change.js
  //   cartPage.updateQuantity(event)                                       — the non-AJAX cart's updates[] copy
  // Both are lexical globals of the theme's classic scripts (assets/booster.js, sections/cart-page.liquid).
  function guardMethod(owner, name, forcedArg) {
    var fn = owner && owner[name];
    if (typeof fn !== 'function' || fn.__hkMinGuard) return;
    var wrapped = function (event) {
      if (forcedArg != null && arguments[forcedArg]) return fn.apply(this, arguments); // an explicit quantity, not the box's
      return admit(event && event.target) ? fn.apply(this, arguments) : undefined;
    };
    wrapped.__hkMinGuard = true;
    owner[name] = wrapped;
  }
  function guardTheme() {
    try { if (typeof BoosterTheme !== 'undefined' && BoosterTheme && BoosterTheme.cart) guardMethod(BoosterTheme.cart, 'updateQuantity', 3); } catch (e) {}
    try { if (typeof cartPage !== 'undefined' && cartPage) guardMethod(cartPage, 'updateQuantity', null); } catch (e) {}
  }
  function guardAll(root) {
    guardTheme();
    var list = (root && root.querySelectorAll) ? root.querySelectorAll(SEL) : [];
    for (var i = 0; i < list.length; i++) guard(list[i]);
    if (root && root.matches && root.matches(SEL)) guard(root);
  }
  guardAll(document);
  if (document.body) liveRegion();
  document.addEventListener('DOMContentLoaded', function () { guardAll(document); liveRegion(); });
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) if (added[j].nodeType === 1) guardAll(added[j]);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // ---- the minus button at the minimum explains instead of acting ----
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.quantity--input button') : null;
    if (!btn) return;
    var input = inputIn(boxOf(btn));
    if (!input) return;
    var min = minOf(input);
    if (!min) return;
    guardTheme(); guard(input);
    if (!isMinus(btn, input)) { window.setTimeout(function () { syncMinus(input); }, 0); return; }
    var v = valueOf(input);
    if (v !== null && v > min) { window.setTimeout(function () { syncMinus(input); }, 0); return; }
    e.preventDefault();
    e.stopImmediatePropagation(); // the theme's inline handler never runs; the box is not rewritten
    syncMinus(input);
    tell(input, min);
  }, true);

  document.addEventListener('focusin', function (e) { if (e.target && e.target.matches && e.target.matches(SEL)) guard(e.target); }, true);
  document.addEventListener('input', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches(SEL)) return;
    guardTheme(); guard(input);
    syncMinus(input); // also when the typed value is under the minimum: the minus button is blocked then
  }, true);

  // on commit (blur / Enter / spinner): under the minimum becomes the minimum, then the theme's own
  // handler runs with that value
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches(SEL)) return;
    guard(input);
    settle(input);
    syncMinus(input);
  }, true);

  // the cart form posts updates[] on Checkout: nothing under a minimum leaves in it
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelectorAll) return;
    var list = form.querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) { settle(list[i]); canonical(list[i]); syncMinus(list[i]); }
  }, true);
})();
